import ArrowLink from 'components/ArrowLink';
import BlockMedia from 'components/BlockMedia';
import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { LayoutContainer } from 'components/Layout';
import Text from 'components/Text';
import type { DigizuiteAsset } from 'models/asset';
import type { Field, SitecoreBlockProps, SitecoreLink } from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';
import { filterTruthy } from 'utils/collection';
import { getAsset } from 'utils/helpers';

interface TeaserItem {
	campaignBubbleLabel?: Field<string>;
	campaignBubbleSale?: Field<boolean>;
	campaignBubbleText?: Field<string>;
	heading?: Field<string>;
	landscapeImage?: Field<DigizuiteAsset[]>;
	link?: Field<SitecoreLink>;
	video?: Field<DigizuiteAsset[]>;
}

interface Props extends SitecoreBlockProps {
	fields?: {
		heading?: Field<string>;
		items?: TeaserItem[];
	};
}

export default function DoubleTeaser({ fields, params }: Props) {
	if (!fields) {
		return null;
	}

	const teasers = filterTruthy(
		fields.items?.map((item) => ({
			campaignBubbleLabel: item.campaignBubbleLabel?.value,
			campaignBubbleSale: Boolean(item.campaignBubbleSale?.value),
			campaignBubbleText: item.campaignBubbleText?.value,
			heading: item?.heading,
			href: item?.link?.value.href,
			image: getAsset('Templated 2:1', item?.landscapeImage),
			linkTarget: item?.link?.value.target,
			linkText: item?.link?.value.text,
		})),
		'href',
		'image',
		'linkText',
	);

	if (teasers.length < 2) {
		return (
			<ComponentPlaceholder
				componentName="DoubleTeaser"
				className={getEditorMargin(params)}
				description="Requires two teaser items with link, link text and image"
			/>
		);
	}

	return (
		<LayoutContainer
			outerClassName={getEditorMargin(params)}
			id={params?.anchor}
			withGrid
		>
			<Text
				className="col-span-full mb-r-md"
				as={params?.heading ?? 'h2'}
				field={fields.heading}
			/>
			{teasers.map(
				({
					campaignBubbleLabel,
					campaignBubbleSale,
					campaignBubbleText,
					heading: teaserHeading,
					href,
					image,
					linkTarget,
					linkText,
				}) => (
					<div
						key={`${href}-${linkText}-${teaserHeading?.value}`}
						className="col-span-full max-md:last:mt-8 md:col-span-6"
					>
						<BlockMedia
							image={{
								src: image.src,
								alt: image.alt,
								variant: 'landscape2x1Half',
							}}
							href={href}
							linkTarget={linkTarget}
							campaignBubbleLabel={campaignBubbleLabel}
							campaignBubbleSale={campaignBubbleSale}
							campaignBubbleText={campaignBubbleText}
							campaignBubbleSmallSize="small"
							campaignBubbleLargeSize="medium"
						/>
						<Text as="h3" field={teaserHeading} className="mt-r-sm" />
						<ArrowLink
							className="mt-r-sm"
							href={href}
							text={linkText}
							target={linkTarget}
						/>
					</div>
				),
			)}
		</LayoutContainer>
	);
}
DoubleTeaser.displayName = 'DoubleTeaser';
